import {
  useCreateCategoryMutation,
  useSetCategoryGroupMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {setCategoryGroups} from 'src/apolloCache/categoryGroups';
import {Button} from 'src/Button';
import InputGroup from 'src/InputGroup';
import {CategoryAppearanceInput} from 'src/inputs/CategoryAppearanceInput';
import {CategoryGroupSelect} from 'src/inputs/CategoryGroupSelect';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type CreateCategoryModalUserProps = {
  onSuccess: () => void;
};

export const CreateCategoryModal = ({
  onRequestClose,
  onSuccess,
}: CreateCategoryModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [icon, setIcon] = React.useState<string | null>(null);
  const [color, setColor] = React.useState<string | null>(null);
  const [groupId, setGroupId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Spans BOTH stages of a submit. createCategory's own loading flag drops
  // once the category exists, while the group assignment may still be in
  // flight — re-enabled buttons there would let a second submit create a
  // duplicate, or Cancel close a modal whose category already exists.
  const [submitting, setSubmitting] = React.useState(false);

  const [createCategory] = useCreateCategoryMutation();
  const [setCategoryGroup] = useSetCategoryGroupMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      const result = await createCategory({
        variables: {
          name: name.trim(),
          description: description.trim() || null,
          icon,
          color,
        },
      });
      const created = result.data?.createCategory;
      if (created && groupId != null) {
        // Best-effort: the category already exists at this point, so a
        // failed group assignment (e.g. the group was deleted concurrently)
        // must not present the whole creation as failed — retrying would
        // hit the unique-name constraint. The category stays ungrouped and
        // can be grouped from its page.
        try {
          await setCategoryGroup({
            variables: {categoryId: created._id, groupId},
            update: (cache, groupResult) => {
              const updated = groupResult.data?.setCategoryGroup;
              if (updated) {
                setCategoryGroups(cache, updated);
              }
            },
          });
        } catch {
          // Ignore — see above.
        }
      }
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={submitting || !name.trim()}
      >
        {submitting ? 'Creating...' : 'Create'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create Category"
      footer={footer}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <InputGroup label="Name">
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g., Groceries, Entertainment"
            autoFocus
          />
        </InputGroup>

        <InputGroup label="Description">
          <Textarea
            value={description}
            onChange={setDescription}
            placeholder="Optional description for this category"
            rows={3}
          />
        </InputGroup>

        <CategoryGroupSelect value={groupId} onChange={setGroupId} />

        <InputGroup label="Appearance">
          <CategoryAppearanceInput
            name={name}
            icon={icon}
            color={color}
            onIconChange={setIcon}
            onColorChange={setColor}
          />
        </InputGroup>
      </form>
    </Modal>
  );
};
